import React, { useEffect, useState, useMemo } from 'react'
import { Box, Button, Card, Grid, Typography } from '@mui/material'
import { useDataProvider, useShowController } from 'react-admin'
import { formatDate, HorizontalCardMedia } from '@thefront/pandipackV2'
import RunList from '../Runs/RunList'
import { useThemeConfig } from '../../themeConfig'
import { Route, Routes, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import LogList from '../log/LogList'

const logStyles = {
    root: {
        '& .MuiCard-root': {
            boxShadow: 'none',
        },
        width: '100%',
    },
    actionGrid: {
        display: 'flex',
        margin: '24px 8px',
    },
    card: {
        flexDirection: 'column',
        flexWrap: 'nowrap',
        justifyContent: 'space-evenly',
        width: '100%',
        height: '100%',
        textAlign: 'left',
    },
    listButton: (theme) => ({
        height: '40px',
        color: theme.palette.secondary.main,
    }),
    loadingTextContainer: {
        margin: '30px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
    },
    linearProgress: {
        width: '40%',
    },
}

export default () => {
    const { configs } = useThemeConfig()
    const { marketplaceSettings, dateFormat } = configs

    const { record, isLoading } = useShowController()

    const [tenant, setTenant] = useState(!isLoading ? record : undefined)

    const dataProvider = useDataProvider()
    const tenantCallback = (event) => {
        setTenant(event.payload)
    }

    useEffect(() => {
        if (record) {
            dataProvider.subscribe(`tenants/${record.id}`, tenantCallback)
            return () =>
                dataProvider.unsubscribe(`tenants/${record.id}`, tenantCallback)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    const lastRun = formatDate(
        tenant?.status?.lastRun?.completionTime,
        false,
        dateFormat
    )
    const navigate = useNavigate()
    const singleIntegrationView = useSelector(
        (state) => state.user.singleIntegrationView
    )

    const [searchLogsView, setSearchLogsView] = useState(
        window.location.href?.includes('/logs')
    )
    const toggleSearchLogsView = () => {
        navigate(searchLogsView ? `/tenants/${tenant.id}/show` : `logs`)
        setSearchLogsView(!searchLogsView)
    }

    return useMemo(() => {
        return (
            tenant && (
                <Box
                    className={'pandium-logs-page'}
                    sx={[
                        logStyles.root,
                        {
                            maxWidth: marketplaceSettings?.fullWidthMarketplace
                                ? 'none'
                                : '1280px',
                        },
                    ]}
                >
                    <Grid
                        size={12}
                        sx={logStyles.actionGrid}
                        className={'pandium-header'}
                    >
                        <Button
                            className={'pandium-back-button'}
                            onClick={() =>
                                marketplaceSettings?.separateApps
                                    ? navigate('/tenants', {
                                          state: singleIntegrationView
                                              ? {
                                                    integration: `${tenant.integration.id}`,
                                                }
                                              : {},
                                      })
                                    : navigate('/integrations')
                            }
                        >
                            {marketplaceSettings?.separateApps
                                ? '< My apps'
                                : '< My apps & integrations'}
                        </Button>
                    </Grid>
                    <Grid container spacing={3} sx={{ alignItems: 'center' }}>
                        <Grid size={3}>
                            <HorizontalCardMedia
                                src={
                                    tenant.integration?.marketplace_settings
                                        ?.primaryLogo
                                }
                            />
                        </Grid>
                        <Grid size={6}>
                            <Typography variant="h4">
                                {tenant.integration?.long_name}
                            </Typography>
                        </Grid>
                    </Grid>
                    <Card sx={logStyles.card}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                margin: '48px 8px 0',
                            }}
                        >
                            <Typography
                                variant="h5"
                                sx={{
                                    fontSize: '24 px',
                                    textTransform: 'capitalize',
                                }}
                            >
                                {searchLogsView
                                    ? 'Search run logs'
                                    : marketplaceSettings.logsLabel ??
                                      'Run logs'}
                            </Typography>
                            <Button
                                variant="outlined"
                                onClick={toggleSearchLogsView}
                            >
                                {searchLogsView
                                    ? 'Show all runs'
                                    : 'Search logs'}
                            </Button>
                        </Box>
                        {/* putting the two lists in separate routes keeps their contexts separate */}
                        <Routes>
                            <Route
                                path="/logs"
                                element={<LogList tenant={tenant} />}
                            />
                            <Route
                                path="/"
                                element={<RunList tenant={tenant} />}
                            />
                        </Routes>
                    </Card>
                </Box>
            )
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastRun, tenant, searchLogsView])
}
